import { Button, Grid, Typography, useTheme } from "@mui/material";
import { GridColumns } from "@mui/x-data-grid";
import React, { useState, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AlertLineURLLogo from "./AlertLineURLLogo";
import { DocumentViewerContainer } from "./styles";
import { useAppSelector } from "../../custom-hooks/redux/redux";
import { AlertLineURLDTO } from "../../models/alert-line-url/alert-line-url-dto";
import { CustomDate } from "../../models/custom-date/custom-date";
import DocumentViewer from "../../reusable-components/DocumentViewer/DocumentViewer";
import { IFile } from "../../reusable-components/DocumentViewer/interfaces";
import SimplePopover from "../../reusable-components/SimplePopover/SimplePopover";
import Table from "../../reusable-components/Table/Table";
import { AlertLineURLApiService } from "../../services/alert-line/alert-line-url-api.service";
import { FT_ALU } from "../../utils/constants/filter-tables-constants";
import { intlFormatDateShort } from "../../utils/time-utils";
import AddEditAlertLineURL from "../AddEditAlertLineURL/AddEditAlertLineURL";
import DialogTitle from "../DialogTitle/DialogTitle";

interface State {
  fileToView: IFile | null;
}

const AlertLineURLsTable = () => {
  const tRP = "components.alert_line_url_table";
  const tRPFields = "components.add_edit_alert_line_url.fields";
  const { t } = useTranslation();
  const theme = useTheme();
  const [state, setState] = useState<State>({
    fileToView: null,
  });

  const { currentUserData } = useAppSelector((state) => state.currentUserReducer);

  const columns: GridColumns<AlertLineURLDTO> = useMemo(
    () => [
      {
        field: "nickname",
        renderHeader: () => <Typography>{`${t(`${tRPFields}.nickname`)}`}</Typography>,
        headerAlign: "center",
        renderCell: ({ row }) => <Typography>{row.nickname}</Typography>,
        flex: 1.5,
        align: "center",
        sortable: true,
      },
      {
        field: "url",
        renderHeader: () => <Typography>{`${t(`${tRPFields}.url`)}`}</Typography>,
        headerAlign: "center",
        renderCell: ({ row }) => <Typography>{row.url}</Typography>,
        flex: 2.5,
      },
      {
        field: "logo",
        renderHeader: () => <Typography>{`${t(`${tRPFields}.logo`)}`}</Typography>,
        headerAlign: "center",
        align: "center",
        renderCell: ({ row }) => <AlertLineURLLogo alertLineURLId={row.id} />,
        flex: 1,
      },
      {
        field: "alertLineURLLanguageVersions",
        renderHeader: () => <Typography>{`${t(`${tRPFields}.language_versions`)}`}</Typography>,
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Grid style={{ display: "flex" }}>
            <Grid style={{ display: "flex", flexDirection: "column" }}>
              {row.alertLineURLLanguageVersions.slice(0, 3).map((langVersion, idx) => (
                <Typography
                  key={idx}
                  style={{ fontWeight: langVersion.isDefault ? 900 : 400 }}
                  title={langVersion.isDefault ? "Default language version" : ""}
                >
                  {langVersion.language?.name}
                </Typography>
              ))}
            </Grid>
            {row.alertLineURLLanguageVersions.length > 3 && (
              <SimplePopover
                buttonType="icon"
                buttonContent={
                  <Grid
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "30px",
                      height: "30px",
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: "30px",
                      padding: "5px",
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    +{row.alertLineURLLanguageVersions.length - 3}
                  </Grid>
                }
              >
                <Grid style={{ display: "flex", flexDirection: "column" }}>
                  {row.alertLineURLLanguageVersions.slice(3).map((langVersion, idx) => (
                    <Typography
                      key={idx}
                      style={{ fontWeight: langVersion.isDefault ? 900 : 400 }}
                    >
                      {langVersion.language?.name}
                    </Typography>
                  ))}
                </Grid>
              </SimplePopover>
            )}
          </Grid>
        ),
        flex: 1,
      },
      {
        field: "validFrom",
        renderHeader: () => <Typography>{`${t(`${tRPFields}.valid_from`)}`}</Typography>,
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Typography>{row.validFrom && intlFormatDateShort(CustomDate.ToDate(row.validFrom)!)}</Typography>
        ),
        flex: 1,
      },
      {
        field: "validTo",
        renderHeader: () => <Typography>{`${t(`${tRPFields}.valid_to`)}`}</Typography>,
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Typography>{row.validTo && intlFormatDateShort(CustomDate.ToDate(row.validTo)!)}</Typography>
        ),
        flex: 1,
      },
      {
        field: "reviewers.asHr",
        renderHeader: () => <Typography>{`${t(`${tRPFields}.hr_reviewers`)}`}</Typography>,
        headerAlign: "center",
        renderCell: ({ row }) => {
          const hrReviewers = row.reviewers.filter((reviewer) => reviewer.asHR);
          return (
            <Grid
              style={{
                display: "flex",
              }}
            >
              <Grid style={{ width: hrReviewers.length > 3 ? "calc(100% - 70px)" : "100%" }}>
                {hrReviewers.slice(0, 3).map((reviewer, idx) => (
                  <Typography
                    key={idx}
                    variant="body2"
                  >
                    {reviewer.user?.name}
                  </Typography>
                ))}
              </Grid>

              {hrReviewers.length > 3 && (
                <SimplePopover
                  buttonType="icon"
                  buttonContent={
                    <Grid
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "30px",
                        height: "30px",
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "30px",
                        padding: "5px",
                        color: theme.palette.primary.contrastText,
                      }}
                    >
                      +{hrReviewers.length - 3}
                    </Grid>
                  }
                >
                  <Grid style={{ display: "flex", flexDirection: "column" }}>
                    {hrReviewers.slice(3).map((reviewer, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                      >
                        {reviewer.user?.name}
                      </Typography>
                    ))}
                  </Grid>
                </SimplePopover>
              )}
            </Grid>
          );
        },
        sortable: false,
        flex: 1.5,
      },
      {
        field: "reviewers.asManager",
        renderHeader: () => <Typography>{`${t(`${tRPFields}.manager_reviewers`)}`}</Typography>,
        headerAlign: "center",
        renderCell: ({ row }) => {
          const managerReviewers = row.reviewers.filter((reviewer) => reviewer.asManager);
          return (
            <Grid
              style={{
                display: "flex",
              }}
            >
              <Grid style={{ width: managerReviewers.length > 3 ? "calc(100% - 70px)" : "100%" }}>
                {managerReviewers.slice(0, 3).map((reviewer, idx) => (
                  <Typography
                    key={idx}
                    variant="body2"
                  >
                    {reviewer.user?.name}
                  </Typography>
                ))}
              </Grid>

              {managerReviewers.length > 3 && (
                <SimplePopover
                  buttonType="icon"
                  buttonContent={
                    <Grid
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "30px",
                        height: "30px",
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "30px",
                        padding: "5px",
                        color: theme.palette.primary.contrastText,
                      }}
                    >
                      +{managerReviewers.length - 3}
                    </Grid>
                  }
                >
                  <Grid style={{ display: "flex", flexDirection: "column" }}>
                    {managerReviewers.slice(3).map((reviewer, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                      >
                        {reviewer.user?.name}
                      </Typography>
                    ))}
                  </Grid>
                </SimplePopover>
              )}
            </Grid>
          );
        },
        sortable: false,
        flex: 1.5,
      },
      {
        field: "reviewers.asCompanyCommitteMember",
        renderHeader: () => <Typography>{`${t(`${tRPFields}.company_commitee_reviewers`)}`}</Typography>,
        headerAlign: "center",
        renderCell: ({ row }) => {
          const ccmReviewers = row.reviewers.filter((reviewer) => reviewer.asCompanyCommitteeMember);
          return (
            <Grid
              style={{
                display: "flex",
              }}
            >
              <Grid style={{ width: ccmReviewers.length > 3 ? "calc(100% - 70px)" : "100%" }}>
                {ccmReviewers.slice(0, 3).map((reviewer, idx) => (
                  <Typography
                    key={idx}
                    variant="body2"
                  >
                    {reviewer.user?.name}
                  </Typography>
                ))}
              </Grid>

              {ccmReviewers.length > 3 && (
                <SimplePopover
                  buttonType="icon"
                  buttonContent={
                    <Grid
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "30px",
                        height: "30px",
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: "30px",
                        padding: "5px",
                        color: theme.palette.primary.contrastText,
                      }}
                    >
                      +{ccmReviewers.length - 3}
                    </Grid>
                  }
                >
                  <Grid style={{ display: "flex", flexDirection: "column" }}>
                    {ccmReviewers.slice(3).map((reviewer, idx) => (
                      <Typography
                        key={idx}
                        variant="body2"
                      >
                        {reviewer.user?.name}
                      </Typography>
                    ))}
                  </Grid>
                </SimplePopover>
              )}
            </Grid>
          );
        },
        sortable: false,
        flex: 1.5,
      },
    ],
    []
  );

  return (
    <>
      {(currentUserData?.user?.id === "1d05696d-7961-43ce-95a6-50bed6b75723" ||
        currentUserData?.user?.id === "7e239fda-07a3-41b0-acf4-ae77870b2ce4") && (
        <Button onClick={() => new AlertLineURLApiService().triggerGenerateDocuments()}> Trigger generate docs </Button>
      )}
      <Table
        filter={FT_ALU}
        translationRootPath={tRP}
        serviceApi={new AlertLineURLApiService()}
        columns={columns}
        CustomAddEditComponent={AddEditAlertLineURL}
        addEditType="custom"
      />
      <DocumentViewerContainer open={!!state.fileToView}>
        <DialogTitle
          title=""
          handleClose={() => {
            setState((state) => ({ ...state, fileToView: null }));
          }}
        />
        <Grid style={{ width: "90vw" }}>
          {state.fileToView && (
            <DocumentViewer
              file={state.fileToView}
              withFooter
            />
          )}
        </Grid>
      </DocumentViewerContainer>
    </>
  );
};

export default memo(AlertLineURLsTable);
